/**
 * Shared `staleTime` tiers for TanStack Query.
 *
 * Before these existed the QueryClient default was `staleTime: 0`, and
 * `refetchOnMount` was left at its default (`true`). Together that meant every
 * query refetched on every component mount — so navigating back to /dashboard
 * refired all 19 metric endpoints, and every dialog re-fetched its dropdowns.
 *
 * The precondition for raising a tier: the data's changes must **originate in
 * this app**. `invalidateQueries` marks a query stale regardless of its
 * staleTime, so a mutation still shows the user their own write — but that only
 * covers writes the frontend makes. Data the backend advances on its own (queue
 * jobs moving a pipeline stage, another operator's session, a queue fed by an
 * upstream stage) has no mutation to invalidate it, so it must opt into `live`
 * or a `refetchInterval` instead of inheriting the default.
 *
 * Pick a tier by how the data changes, not by how important it feels.
 */
export const STALE_TIME = {
  /**
   * Opt out — refetch on every mount, as the default was before these tiers.
   * For data driven by a `refetchInterval` or an SSE stream (where the poll,
   * not staleness, decides), and for server-advanced data that no mutation in
   * this app marks stale.
   */
  live: 0,

  /** Paginated listings. Matches the QueryClient default. */
  list: 30_000,

  /** Dashboard KPI tiles. The backend caches these for 60s anyway. */
  metrics: 60_000,

  /**
   * Heavier aggregate stats — shelf / storage-location utilisation averages.
   * Tracks the backend's `cache_ttl.stats` (120s): refetching sooner just
   * re-reads the payload the server already has cached. Keep the two numbers
   * comparable if either side moves.
   */
  stats: 120_000,

  /** Reference data behind dropdowns: container types, roles, devices. */
  reference: 5 * 60_000,

  /**
   * Data that only changes on deploy (e.g. the nationalities list).
   *
   * Deliberately `Infinity` and NOT the literal `'static'`. They are not
   * interchangeable: `Infinity` suppresses staleness-driven refetches but still
   * honours `invalidateQueries`, whereas `'static'` blocks every refetch
   * including manual invalidation — which would silently break the mutation
   * invalidation this whole scheme leans on. Do not "simplify" this to
   * `'static'`.
   */
  frozen: Number.POSITIVE_INFINITY
} as const;
